import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  useEffect, useState, useCallback, memo, Fragment, useMemo,
} from 'react';
import { useRouter } from 'next/router';
import {
  CircularProgress,
} from '@material-ui/core';

import clsx from 'clsx';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { Info } from '@material-ui/icons';
import {
  SearchStatus, BookingAction, PageLinks,
  AgencyPlanningType,
  getSupportedParams, PaymentTypeCodes,
} from '../../constants';
import getCruisePriceBreakdownTotals from '../../helpers/getCruisePriceBreakdownTotals';
import getPriceBreakdownKey from '../../helpers/getPriceBreakdownKey';
import { priceRequestTakeLatest, priceSuccess, setStateroomTab } from '../../store/slices/cruise';
import {
  addToTrip, resetError, setBookingAction, removeFromTrip,
  bookTripRequest, removeTripConfirmation, setCruiseItineraryPaymentAmount,
  resetSaveTripStatus,
  changeTrip,
  discardTripConfirmationDiningChanges,
  setSelfBookingAllowed,
} from '../../store/slices/trip';
import CruiseLineAdvisories from '../CruiseLineAdvisories';
import withSpinner from '../../HOCs/withSpinner';
import AdditionalInfoModal from '../TravelOption/AdditionalInfoModal';
import PromotionInfo from '../TravelOption/PromotionInfo';
import TripUpdateConfirmModal from './TripUpdateConfirmModal';
import getTotalPriceBreakdown from '../../helpers/getTotalPriceBreakdown';
import formatPriceRequest from '../../helpers/formatPriceRequest';
import ViewTripConfigurationItems from './ViewTripConfigurationItems';
import computeCruiseTotals from '../../helpers/computeCruiseTotals';
import formatTrip from '../../helpers/formatTrip';
import getTripModes from '../../helpers/getTripModes';
import EditableField from '../EditableField';
import ResetTripButton from './ResetTripButton';
import Label from '../Label';
import formatBookButtonTextByTrip from '../../helpers/formatters/formatBookButtonTextByTrip';
import TripBookButton from './ViewTripConfigurationItems/TripBookButton';
import TripConfirmationChanges from './TripConfirmationChanges';
import getTripConfirmationChanges from './TripConfirmationChanges/getTripConfirmationChanges';
import Button from '../Button';
import { checkDoEnableSelectFlightsBySegment, checkDoShowAgentCommission, checkDoShowTripConfirmationPricing } from '../../store/slices/user';
import useResetSaveTripStatus from '../../hooks/useResetSaveTripStatus';
import currencySymbol from '../../helpers/currencySymbol';
import getHotelItinerariesByPaymentType from '../../helpers/getHotelItinerariesByPaymentType';
import MarkdownToHtml from '../MarkdownToHtml';
import evaluateSeatSelectionWarning from '../../helpers/evaluateSeatSelectionWarning';
import getCurrentPageURL from '../../helpers/getCurrentPageURL';
import DisplayCurrencyMessage from '../TravelOption/HotelInformation/HotelItinerary/DisplayCurrencyMessage';
import getHotelAndRoomNames from '../../helpers/getHotelAndRoomNames';
import { shouldShowField } from '../CruiseBooking/AdditionalCheckoutFields';
import checkDoEnableTripConfigDecoupling from '../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import { setFlightTripSearchInputs } from '../../store/slices/tripSearch';
import flightSearchInput from '../../helpers/selectors/flightSearchInput';
import formatTravelMode from '../../helpers/formatTravelMode';
import { usePayment } from '../CruiseBooking/PaymentConfig/AddCardForm/VgsIFrameInputProvider';

const TripConfiguration = ({
  tripId,
  isTripSearch,
  updatedItineraries,
  doShowConfirmedTrip,
  sectionHeader,
  onTabChange,
}) => {
  const router = useRouter();
  const storeTrip = useSelector((state) => state.trip.selectedTrips[tripId] || {});
  const isConfirmationPage = router.pathname.indexOf('confirmation') > -1;
  const isCheckoutPage = router.pathname.indexOf('checkout') > -1;
  const { pricingStatus } = useSelector((store) => store.cruise);
  const {
    passengers, agencyPlanningFees, doChargeAgencyPlanningFees, tripConfirmation,
    agencyPlanningType, selectedAdditionalCheckoutFields,
  } = useSelector((store) => store.trip);

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  const doShowAgentCommission = useSelector(checkDoShowAgentCommission);
  const {
    isLoggedIn, email: loggedInUserEmail, travelAgency = {},
  } = useSelector((store) => store.user);

  const hasTripConfirmation = tripConfirmation != null
    && tripConfirmation.bookingResponses != null
    && tripConfirmation.bookingResponses[0] != null;
  const isConfirmation = hasTripConfirmation && doShowConfirmedTrip;
  const [trip, setTrip] = useState(storeTrip);
  const isUpdatingBookedTrip = trip && trip.isUpdatingBookedTrip;
  const saveTripStatus = storeTrip && storeTrip.saveTripStatus;
  const doShowPricing = useSelector(checkDoShowTripConfirmationPricing);
  let doHideTripConfigPayNowLater = travelAgency && travelAgency.settings
    && travelAgency.settings.doHideTripConfigPayNowLater;
  const tripQuoteSettings = (travelAgency && travelAgency.settings
    && travelAgency.settings.tripQuoteSettings) || {};
  const doShowWarningOutsideTripConfig = tripQuoteSettings.doShowWarningOutsideTripConfig || false;
  // const doHidePayNowOnDeposit = travelAgency && travelAgency.settings
  //   && travelAgency.settings.hidePayNowOnDeposit;
  const doHidePayNowWhenHotelAdded = travelAgency && travelAgency.settings
    && travelAgency.settings.hidePayNowWhenHotelAdded;
  const isTripQuote = tripConfirmation
    && tripConfirmation.tripStatus === BookingAction.SEND_QUOTE;
  const bookButtonLabel = isTripQuote && tripQuoteSettings.bookButtonLabel ? tripQuoteSettings.bookButtonLabel : 'Book';
  const supportedUrlParams = travelAgency && travelAgency.settings
    && travelAgency.settings.supportedUrlParams;
  // if (doHidePayNowOnDeposit) {
  //   const depositHotels = getHotelItinerariesByPaymentType(trip, PaymentTypeCodes.DEPOSIT);
  //   doHideTripConfigPayNowLater = depositHotels.length > 0;
  // }
  if (doHidePayNowWhenHotelAdded) {
    const guaranteeHotels = getHotelItinerariesByPaymentType(trip);
    if (guaranteeHotels.length > 0) {
      doHideTripConfigPayNowLater = true;
    }
  }

  const {
    doShowSeatSelectionWarning, seatSelectionWarning,
  } = evaluateSeatSelectionWarning(passengers, travelAgency, trip, tripConfirmation,
    isCheckoutPage);
  const { itinerariesOnHold, itinerariesToBook } = getTripModes(trip, travelAgency);

  useEffect(() => {
    if (doShowConfirmedTrip && hasTripConfirmation) {
      setTrip({
        ...formatTrip({ trip: tripConfirmation && tripConfirmation.trip, tripConfirmation }),
        addPaymentMethodToTravelTypes: storeTrip && storeTrip.addPaymentMethodToTravelTypes,
      });
    } else {
      setTrip(storeTrip);
    }
  }, [doShowConfirmedTrip, hasTripConfirmation, storeTrip, tripConfirmation]);

  const {
    saveSendTripEmailStatus, error, saveSendTripEmailError,
  } = useSelector((store) => store.trip);
  const priceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);
  const [doShowFullError, setDoShowFullError] = useState(true);
  const cruiseDetails = useSelector((store) => store.cruise.cruiseDetails);
  const [cruiseTotals, setCruiseTotals] = useState(getCruisePriceBreakdownTotals(null));
  const dispatch = useDispatch();
  const {
    test,
    doReturnAvailabilityError,
    doReturnPriceChangeError,
    doShowErrorOnPageLoad,
    travelAgencyAliasName,
    advisor,
  } = router.query;

  let hotelItinerariesArray = [];
  if (trip && trip.hotelItineraries) {
    if (Array.isArray(trip.hotelItineraries)) {
      hotelItinerariesArray = trip.hotelItineraries;
    } else {
      hotelItinerariesArray = Object.values(trip.hotelItineraries);
    }
  }

  const pricingKey = trip && trip.cruiseItinerary
    && getPriceBreakdownKey(formatPriceRequest({
      itinerary: trip.cruiseItinerary,
      passengers,
      bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
    }));
  const { track } = useAnalytics();
  const pricing = priceBreakdowns[pricingKey] || {};

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [travelModeToRemove, setTravelModeToRemove] = useState(null);
  const [itineraryIdToRemove, setItineraryIdToRemove] = useState(null);
  const tripChanges = tripConfirmation ? getTripConfirmationChanges(passengers,
    trip && trip.addPaymentMethodToTravelTypes,
    storeTrip, tripConfirmation) : {};
  const doShowBookButton = (!isTripSearch && !isConfirmation && !updatedItineraries
    && (!tripConfirmation || tripConfirmation.tripStatus !== BookingAction.ADVICE))
    || tripChanges.hasChanges;

  const { selectedFlightSegments } = useSelector(flightSearchInput(tripId, isTripSearch));

  const handleResetClick = () => {
    dispatch(addToTrip({ tripId, trip: null }));
    dispatch(setStateroomTab({
      tabNumber: 0,
      doScroll: false,
    }));
    if (doEnableSelectFlightsBySegment) {
      if (selectedFlightSegments && Object.values(selectedFlightSegments).length) {
        const selectedFlightSegments2 = { ...selectedFlightSegments };
        delete selectedFlightSegments2[Object.values(selectedFlightSegments).length - 1];
        dispatch(setFlightTripSearchInputs({
          selectedFlightSegments: selectedFlightSegments2,
        }));
      }
    }
    track('tripConfiguration.handleResetClick');
  };

  const toggleViewConfirmModal = () => {
    setIsConfirmModalOpen((v) => !v);
  };

  const formatNumber = (number, className, title, currencyCode) => <NumberFormat value={number} prefix={currencySymbol(currencyCode)} displayType="text" thousandSeparator fixedDecimalScale="true" decimalScale="2" className={className} title={title} />;

  const formatAmountWithCurrency = (items, pricePropertyName = 'price') => {
    if (!items || !items.length) return '';
    const itemsGroup = _.groupBy(_.filter(items, (it) => it[pricePropertyName] > 0), (grp) => grp.currencyCode || 'USD');
    if (itemsGroup && Object.keys(itemsGroup).length === 1) {
      const currency = Object.keys(itemsGroup)[0];
      return formatNumber(Number(_.sumBy(itemsGroup[currency], pricePropertyName)),
        undefined, undefined, currency);
    }
    return _.map(_.groupBy(_.filter(items, (it) => it[pricePropertyName] > 0), (grp) => grp.currencyCode || 'USD'), (r, key) => currencySymbol(key) + Number(_.sumBy(r, pricePropertyName)).toFixed(2).toLocaleString()).join(' + ');
  };

  const formatZeroAmount = useCallback((totalItems) => {
    let code = 'USD';
    if (totalItems && totalItems.length) {
      const group = _.groupBy(totalItems, (t) => t.currencyCode || 'USD');
      const keys = Object.keys(group);
      if (keys.length === 1) code = _.first(keys);
      else if (keys.length > 1 && !group.USD) {
        code = _.orderBy(_.map(group, (g, key) => ({ code: key, value: g.length })), 'value', 'desc')[0].code;
      }
    }
    return `${currencySymbol(code)}0`;
  }, []);

  const handleRemoveClick = (key, index, mode, id) => {
    if (!isTripSearch) {
      /// Handle trip update in checkout/confirmation page.
      /// we need to update trip in backend in this case
      setTravelModeToRemove(mode);
      setItineraryIdToRemove(id);
      toggleViewConfirmModal();
    } else {
      dispatch(removeFromTrip({ tripId, key, id }));
      track('tripConfiguration.handleRemoveClick', {
        tripId, key, index,
      });
    }
  };

  const labelText = useMemo(() => {
    if (error && (error.length - 100) > 20) {
      return (
        <div>
          {doShowFullError ? <MarkdownToHtml content={error.replaceAll('\n', '<br />')} className="text-orange-700" /> : <MarkdownToHtml className="text-orange-700" content={`${error.slice(0, 100)}...`} />}
          <button
            type="button"
            onClick={() => setDoShowFullError((v) => !v)}
            className="text-end text-sm text-blue-500 ml-1 border-none focus:outline-none"
          >
            {doShowFullError ? 'Show less' : 'Show more'}
          </button>
        </div>
      );
    }
    return error;
  }, [doShowFullError, error]);
  const payment = usePayment();
  const { tokenizeFunc, isFormValid } = payment || {};
  const handleBook = useCallback(() => {
    const doMoveToCheckout = tripConfirmation
      && tripConfirmation.tripStatus === BookingAction.SEND_QUOTE
      && !isCheckoutPage;

    if (doMoveToCheckout) {
      let newUrl = router.asPath.replace(`${travelAgencyAliasName}/confirmation`, `${travelAgencyAliasName}/checkout`);
      dispatch(setSelfBookingAllowed());
      const url = new URL(newUrl, window.location.origin);
      /// Adding "tripquote" query param to not prompt password on checkout page when booking quote
      url.searchParams.set('tripquote', '');
      newUrl = url.toString().replace(window.location.origin, '');
      dispatch(removeTripConfirmation());
      dispatch(setBookingAction(BookingAction.BOOK));
      router.push(newUrl);
    } else if (tripChanges.hasChanges) {
      dispatch(bookTripRequest({
        tripId,
        tripConfirmationChanges: tripChanges,
        tokenizeFunc,
        isFormValid,
      }));
    } else {
      const doShowErrorForTesting = test === 'true' && doShowErrorOnPageLoad !== 'true';
      const trackingParams = {
        ...getSupportedParams(supportedUrlParams, router),
        ...(selectedAdditionalCheckoutFields
          ? Object.fromEntries(
            Object.entries(selectedAdditionalCheckoutFields).map(
              ([key, value]) => [key, _.isArray(value) ? value : [value]],
            ),
          )
          : {}),
      };
      const filterTrackingParams = (checkoutFields, trackingParameters) => {
        if (!Array.isArray(checkoutFields)) return trackingParameters;
        const filteredParams = { ...trackingParameters };
        checkoutFields.forEach((field) => {
          if (!shouldShowField(field, selectedAdditionalCheckoutFields)
            || !selectedAdditionalCheckoutFields?.[field?.key]) {
            delete filteredParams[field.key];
          }
        });
        return filteredParams;
      };
      const cleanedParams = filterTrackingParams(travelAgency?.checkoutFields, trackingParams);
      dispatch(bookTripRequest({
        tripId,
        doReturnAvailabilityError: doShowErrorForTesting && doReturnAvailabilityError === 'true',
        doReturnPriceChangeError: doShowErrorForTesting && doReturnPriceChangeError === 'true',
        isUpdatingExistingBooking: isUpdatingBookedTrip && tripConfirmation.tripId,
        trackingParams: cleanedParams,
        tokenizeFunc,
        isFormValid,
      }));
    }
    track(`tripConfiguration.handleBook${doMoveToCheckout ? '.moveToCheckoutPageForBookingQuote' : ''}`, {
      tripId,
      loggedInUserEmail,
      page: getCurrentPageURL(),
      tripConfirmationChanges: tripChanges && tripChanges.hasChanges ? tripChanges : undefined,
    });
  }, [tripConfirmation, tripChanges, track, tripId,
    dispatch, router, travelAgencyAliasName, test, doShowErrorOnPageLoad,
    doReturnAvailabilityError, doReturnPriceChangeError, selectedAdditionalCheckoutFields,
    isUpdatingBookedTrip, supportedUrlParams, loggedInUserEmail, travelAgency.checkoutFields,
    tokenizeFunc, isFormValid]);

  useEffect(() => {
    if (!trip || !trip.cruiseItinerary) {
      return;
    }

    const { cabins } = trip.cruiseItinerary;
    if (!cabins || !cabins.length) {
      return;
    }

    const { selectedShipCabinCategory, selectedRateOption } = cabins[0];
    const { resultno, sessionKey } = selectedShipCabinCategory || {};
    const { rateOptionCode, gradeno } = selectedRateOption || {};
    const farecode = rateOptionCode;

    if (isTripSearch || !cabins[0].pricing) {
      dispatch(
        priceRequestTakeLatest({
          ...formatPriceRequest({
            itinerary: trip.cruiseItinerary,
            passengers,
            bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
          }),
          gradeno,
          resultno,
          farecode,
          sessionKey,
        }),
      );
    } else if (!priceBreakdowns[pricingKey]) {
      dispatch(
        priceSuccess({
          [pricingKey]: cabins.map((cabin) => cabin.pricing),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTripSearch, passengers, pricingKey, trip]);

  useEffect(() => {
    if (saveTripStatus === SearchStatus.DONE && !doShowConfirmedTrip) {
      if (router.pathname === PageLinks.Home.url) {
        if (storeTrip.tripCode) {
          const query = {
            tripId: storeTrip.tripCode,
          };
          if (test === 'true') {
            query.test = true;
          }
          const supportedParams = getSupportedParams(supportedUrlParams, router);
          Object.assign(query, supportedParams);

          let pathname = '/checkout';
          if (advisor) query.advisor = advisor;
          if (!isLoggedIn) {
            pathname = `${travelAgency.agencyAliasName}${pathname}`;
          }
          router.push({
            pathname,
            query,
          });
        }

        if (!storeTrip.tripCode) {
          // Reset error after 3s
          setTimeout(() => {
            dispatch(resetError());
          }, 5000);
        }
      } else {
        dispatch(resetSaveTripStatus({ tripId }));
        if (storeTrip.relatedTripId) {
          dispatch(resetSaveTripStatus({ tripId: storeTrip.relatedTripId }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doShowConfirmedTrip, error, saveTripStatus, storeTrip.tripCode, test, tripId]);

  useResetSaveTripStatus(saveSendTripEmailStatus, saveSendTripEmailError);

  useEffect(() => {
    setCruiseTotals(computeCruiseTotals({
      trip,
      priceBreakdowns,
      passengers,
      bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
    }));
  }, [passengers, priceBreakdowns, trip, tripConfirmation]);

  const checkoutButtonLabel = () => {
    if (tripConfirmation && tripChanges.hasChanges) {
      if (tripChanges.doOnlyAddPaymentMethod) return 'Submit payment';
      if (tripChanges.doAddPaymentMethod) return 'Submit changes & payment';
      return 'Submit changes';
    }
    if (trip.doChangeItineraryInBookedTrip) return `Change ${trip.bookedModes}`;
    if (trip.doAddItineraryInBookedTrip) return `Add ${trip.bookedModes}`;

    if (itinerariesOnHold.length === 0) return bookButtonLabel;
    if (itinerariesToBook.length === 0) return formatBookButtonTextByTrip(trip, travelAgency);
    return `Book Trip (${formatBookButtonTextByTrip(trip, travelAgency)})`;
  };

  const handleSetPaymentAmount = (roomIndex) => (paymentAmount) => {
    dispatch(setCruiseItineraryPaymentAmount({ tripId, paymentAmount, roomIndex }));
  };

  const passengersCount = useMemo(() => Object.keys(passengers || {}).length, [passengers]);
  const getAgencyPlanningFees = useCallback(() => {
    if (doShowConfirmedTrip) {
      if (tripConfirmation.agencyPlanningType === AgencyPlanningType.AgencyPlanningFeesPerPassenger
        && passengersCount > 0) {
        return tripConfirmation.agencyPlanningFees * passengersCount;
      }
      return tripConfirmation.agencyPlanningFees;
    }
    if (doChargeAgencyPlanningFees) {
      if (agencyPlanningType === AgencyPlanningType.AgencyPlanningFeesPerPassenger
        && passengersCount > 0) {
        return agencyPlanningFees * passengersCount;
      }
      return agencyPlanningFees;
    }
    return 0;
  }, [agencyPlanningFees, agencyPlanningType,
    doChargeAgencyPlanningFees, doShowConfirmedTrip, passengersCount, tripConfirmation]);

  const totalPriceBreakdown = useMemo(() => getTotalPriceBreakdown(trip, cruiseTotals,
    tripConfirmation && doShowConfirmedTrip ? tripConfirmation.travelers : passengers,
    isTripSearch, doShowConfirmedTrip && tripConfirmation,
    getAgencyPlanningFees(), travelAgency.carDirectBillingVendors, travelAgency),
  [doShowConfirmedTrip, getAgencyPlanningFees, isTripSearch, passengers,
    cruiseTotals, travelAgency, trip, tripConfirmation]);

  const { doDisableBookButton, disableBookBtnTooltip } = useMemo(() => {
    try {
      const disabledModes = travelAgency?.disabledBookingTransportModes || [];

      if (!isCheckoutPage || !disabledModes.length) {
        return { doDisableBookButton: false, disableBookBtnTooltip: false };
      }
      const bookedModes = trip?.bookedTravelTypes || [];
      const disabledBooked = bookedModes.filter((m) => disabledModes.includes(m));
      const allBookedDisabled = bookedModes.length > 0
        && disabledBooked.length === bookedModes.length;
      const someBookedDisabled = disabledBooked.length > 0 && !allBookedDisabled;
      const disabledHold = itinerariesOnHold.filter((m) => disabledModes.includes(m));
      const disabledBook = itinerariesToBook.filter((m) => disabledModes.includes(m));

      const formatModes = (modes) => modes.map(formatTravelMode).join(', ');
      let tooltip = '';

      if (bookedModes.length === 1 && disabledBooked.length && itinerariesOnHold.length === 0) {
        tooltip = `Booking ${formatModes(bookedModes)} is disabled.`;
      } else if (disabledHold.length && !itinerariesToBook.length) {
        tooltip = `Holding ${formatModes(disabledHold)} is disabled.`;
      } else if (disabledHold.length && disabledBook.length) {
        tooltip = `Holding ${formatModes(disabledHold)} and Booking ${formatModes(disabledBook)} is disabled. Please remove them to continue.`;
      } else if (allBookedDisabled) {
        tooltip = `All selected trip modes (${formatModes(bookedModes)}) are disabled for booking.`;
      } else if (someBookedDisabled) {
        tooltip = `You are not allowed to book ${formatModes(disabledBooked)}. Please remove to continue.`;
      } else if (disabledBook.length && !itinerariesOnHold.length) {
        tooltip = `You are not allowed to book ${formatModes(disabledBook)}.`;
      }

      return {
        doDisableBookButton: !!tooltip,
        disableBookBtnTooltip: tooltip,
      };
    } catch (e) {
      return { doDisableBookButton: false, disableBookBtnTooltip: false };
    }
  }, [itinerariesOnHold, itinerariesToBook, travelAgency, trip, isCheckoutPage]);

  const hasServiceFee = totalPriceBreakdown
    && totalPriceBreakdown.total
    && Array.isArray(totalPriceBreakdown.total.totalItems)
    && totalPriceBreakdown.total.totalItems.length > 0
    ? totalPriceBreakdown.total.totalItems.some(
      (item) => item.title && item.title.toLowerCase() === 'service fee',
    ) : false;

  const hasCruisePriceBreakdownErrors = cruiseTotals && cruiseTotals.errors
    && cruiseTotals.errors.length > 0;

  const handleDiscardChanges = () => {
    dispatch(discardTripConfirmationDiningChanges());
    const updatedPassengers = { ...passengers };
    _.each(updatedPassengers, (p) => {
      const pIndex = p.passengerIndex;
      updatedPassengers[pIndex] = {
        ...updatedPassengers[pIndex],
        insuranceCoverage: tripConfirmation.travelers[pIndex].insuranceCoverage,
        travelerPreferences: {
          ...updatedPassengers[pIndex].travelerPreferences,
          selectedPackages: [...(tripConfirmation
            .travelers[pIndex].travelerPreferences.selectedPackages || [])],
          selectedSpecialServices: [...(tripConfirmation
            .travelers[pIndex].travelerPreferences.selectedSpecialServices || [])],
          cruiseTransportPreference: tripConfirmation.travelers[pIndex]
            .travelerPreferences.cruiseTransportPreference,
        },
      };
    });
    dispatch(changeTrip({
      field: 'passengers',
      value: updatedPassengers,
    }));
    dispatch(changeTrip({ field: 'isManagingServices', value: false }));
    dispatch(changeTrip({ field: 'isManagingInsurance', value: false }));
    dispatch(changeTrip({ field: 'isManagingTransport', value: false }));
    dispatch(changeTrip({ field: 'isManagingPackage', value: false }));
  };

  const depositDetails = useMemo(() => {
    try {
      const hotelItinerariesWithRequiredDeposit = getHotelItinerariesByPaymentType(
        trip,
        PaymentTypeCodes.DEPOSIT,
      );
      let depositInfoArray = [];

      if (hotelItinerariesWithRequiredDeposit.length > 0) {
        depositInfoArray = hotelItinerariesWithRequiredDeposit
          .filter((itinerary) => itinerary && itinerary.selectedProduct)
          .map((itinerary) => {
            let depositInfo = '';
            const {
              additional_info: additionalInfo,
              deposit_info: depositInfoRaw,
            } = itinerary.selectedProduct;
            if (depositInfoRaw) {
              depositInfo = depositInfoRaw;
            } else if (additionalInfo) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(additionalInfo, 'text/html');
              const depositDiv = Array.from(doc.querySelectorAll('div')).find((div) => div.textContent.includes('Deposit information:'));
              depositInfo = depositDiv ? depositDiv.textContent.replace(/Deposit information:/, '').trim() : '';

              if (!depositInfo) {
                const cancellationEle = _.findLast(Array.from(doc.querySelectorAll('div')), (div) => div.textContent.includes('Cancellation information:'));
                if (cancellationEle) {
                  const cancelPolicy = cancellationEle ? cancellationEle.textContent.replace(/Cancellation information:/, '').trim() : '';
                  if (cancelPolicy && cancelPolicy.toUpperCase().includes('DEPOSIT')) {
                    depositInfo = cancelPolicy.replace('PRCNT', 'PERCENT');
                  }
                }
              }
              if (!depositInfo) {
                const guaranteeEle = _.findLast(Array.from(doc.querySelectorAll('div')),
                  (div) => div.textContent.includes('Guarantee information:'));
                if (guaranteeEle) {
                  const guaranteeInfo = guaranteeEle ? guaranteeEle.textContent.replace(/Guarantee information:/, '').trim() : '';
                  if (guaranteeInfo && (guaranteeInfo.toUpperCase().includes('FULL AMOUNT')
                || guaranteeInfo.toUpperCase().includes('IN ADVANCE')
                || guaranteeInfo.toUpperCase().includes('BOOKING TIME'))) {
                    depositInfo = guaranteeInfo.replace('PRCNT', 'PERCENT');
                  }
                }
              }
            }

            if (depositInfo) {
              return `Hotel (${getHotelAndRoomNames(itinerary)}) requires a deposit. ${depositInfo}`;
            }
            return `Hotel (${getHotelAndRoomNames(itinerary)}) requires a deposit (check rate description - card will be charged immediately)`;
          });

        if (depositInfoArray.length > 0) {
          return depositInfoArray.map((info, infoIndex) => (
            <Fragment key={`deposit-info-${info}-${infoIndex.toString()}`}>
              <Label
                text={`${info.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}`}
                level="warning"
                className="text-sm"
              />
              <br />
            </Fragment>
          ));
        }
        return (
          <Label
            text="Deposit required (check rate description - card will be charged immediately)"
            level="warning"
            className="text-sm"
          />
        );
      }
    } catch (e) {
      console.log(e);
    }
    return <></>;
  }, [trip]);

  const renderComponent = () => (
    <>
      {isConfirmModalOpen
        && (
          <TripUpdateConfirmModal
            isModalOpen={isConfirmModalOpen}
            tripId={tripId}
            travelModeToRemove={travelModeToRemove}
            itineraryId={itineraryIdToRemove}
            toggleViewConfirmModal={toggleViewConfirmModal}
          />
        )}
      <div className={clsx({ 'border-2 bg-white': isTripQuote && doShowWarningOutsideTripConfig })}>
        <div className={clsx('px-2 pb-4 lg:pt-8 md:pt-4 pt-2 bg-white', { 'rounded-t-md': doEnableTripConfigDecoupling })}>
          <div className="sm:flex align-middle mb-4">
            <div className="flex-auto font-bold text-sm xl:text-lg leading-5">
              <div className="text-subHeading font-subHeading">
                {sectionHeader || 'Your Trip Configuration:'}
              </div>
            </div>
            {isTripSearch && <ResetTripButton onResetClick={handleResetClick} />}
          </div>

          {!hasCruisePriceBreakdownErrors && (
            <ViewTripConfigurationItems
              tripId={tripId}
              isTripSearch={isTripSearch}
              onRemoveClick={handleRemoveClick}
              cruiseTotals={cruiseTotals}
              updatedItineraries={updatedItineraries}
              doShowConfirmedTrip={doShowConfirmedTrip}
              onTabChange={onTabChange}
              doShowAgentCommission={doShowAgentCommission}
              totalPriceBreakdown={totalPriceBreakdown}
            />
          )}

          {tripConfirmation && (
            <TripConfirmationChanges tripId={tripId} />
          )}
        </div>
        {!hasCruisePriceBreakdownErrors && (
          <>
            <div className={clsx('py-6 px-4', { ' bg-white mb-5 rounded-b-md': doEnableTripConfigDecoupling, 'bg-body-0': isTripSearch && !doEnableTripConfigDecoupling })}>
              {doShowPricing && (
                <div className="grid divide-y">
                  <div className="grid grid-cols-3 py-2">
                    <div className="font-bold text-sm xl:text-lg leading-5">
                      <span className="text-subHeading font-subHeading">
                        {totalPriceBreakdown.total.title}
                      </span>
                    </div>
                    <div className="font-bold text-sm xl:text-lg leading-5 text-right col-span-2">
                      {doShowConfirmedTrip && totalPriceBreakdown.total.oldPrice > 0
                    && totalPriceBreakdown.total.oldPrice !== totalPriceBreakdown.total.price && (
                        formatNumber(totalPriceBreakdown.total.oldPrice, 'line-through pr-1 text-sm', 'Trip price before changes', 'USD')
                      )}
                      {formatAmountWithCurrency(totalPriceBreakdown.total.totalItems)}
                      {hasServiceFee && (
                      <div className="text-xs font-normal text-gray-600">
                        <div className="text-right">Includes taxes & fees</div>
                      </div>
                      )}
                    </div>
                  </div>
                  {doHideTripConfigPayNowLater && (
                    <div className="py-2 text-xs xl:text-sm leading-5">
                      {(totalPriceBreakdown.total.totalItems || []).map((it, index) => (
                        <div className="flex justify-between pb-2 text-xs text-gray-600" key={`${it.title}-${it.price}-${index.toString()}`}>
                          <div>
                            {it.title}
                          </div>
                          <div className="text-right">
                            {formatNumber(it.price, undefined, undefined, it.currencyCode)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!hasTripConfirmation || doHideTripConfigPayNowLater ? <></> : (
                    <div>
                      <div className="flex justify-between py-2 font-bold text-xs xl:text-sm leading-5">
                        <div className="text-smallerHeading font-smallerHeading">
                          Total Paid
                        </div>
                        <div className="text-right">
                          {totalPriceBreakdown.paid.price === 0
                            ? formatZeroAmount(totalPriceBreakdown.total.totalItems)
                            : formatAmountWithCurrency(totalPriceBreakdown.paid.paidItems)}
                        </div>
                      </div>
                      {totalPriceBreakdown.paid.paidItems.map((it, index) => (
                        <div className="flex justify-between pb-2 text-xs text-gray-600" key={`${it.title}-${it.price}-${index.toString()}`}>
                          <div>
                            {it.title}
                          </div>
                          <div className="text-right">
                            {formatNumber(it.price, undefined, undefined, it.currencyCode)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!doHideTripConfigPayNowLater
                    && (!isConfirmation || tripChanges.doAddPaymentMethod) ? (
                      <div>
                        <div className="flex justify-between py-2 font-bold text-xs xl:text-sm leading-5">
                          <div className="text-smallerHeading font-smallerHeading">
                            {!isConfirmation || tripChanges.doAddPaymentMethod ? totalPriceBreakdown.dueNow.title : 'Total Paid'}
                          </div>
                          <div className="text-right">
                            {totalPriceBreakdown.dueNow.price === 0
                              ? formatZeroAmount(totalPriceBreakdown.total.totalItems)
                              : formatAmountWithCurrency(totalPriceBreakdown.dueNow.dueNowItems)}
                          </div>
                        </div>
                        {totalPriceBreakdown.dueNow.dueNowItems.map((it, index) => (
                          <div className="flex justify-between pb-2 text-xs text-gray-600" key={`${it.title}-${it.price}-${index.toString()}`}>
                            <div>
                              {it.title}
                            </div>
                            <div className="text-right">
                              {it.isEditable && isLoggedIn && !isTripSearch
                              && (!doShowConfirmedTrip || tripChanges.doAddPaymentMethod)
                                ? (
                                  <EditableField
                                    defaultValue={trip && trip.cruisePayments
                                    && _.find(trip.cruisePayments, (c) => c && c.roomIndex
                                      === it.roomIndex) ? _.find(trip.cruisePayments, (c) => c
                                        && c.roomIndex === it.roomIndex).paymentAmount : it.price}
                                    minValue={it.minValue}
                                    maxValue={it.maxValue}
                                    formatter={formatNumber}
                                    onSave={handleSetPaymentAmount(it.roomIndex)}
                                  />
                                ) : formatNumber(it.price, undefined, undefined, it.currencyCode)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <></>}
                  {!doHideTripConfigPayNowLater
              && (!tripConfirmation || tripConfirmation.tripStatus !== BookingAction.CANCELLED)
                    && (
                      <div className="text-xs xl:text-sm leading-5">
                        <div className="flex justify-between py-2 font-bold">
                          <div>
                            <span className="text-smallerHeading font-smallerHeading">
                              {totalPriceBreakdown.paymentLater.title}
                            </span>
                          </div>
                          <div className="text-right">
                            {totalPriceBreakdown.paymentLater.price === 0
                              ? formatZeroAmount(totalPriceBreakdown.total.totalItems)
                              : formatAmountWithCurrency(
                                totalPriceBreakdown.paymentLater.payLaterItems,
                              )}
                          </div>
                        </div>
                        {(totalPriceBreakdown.paymentLater.payLaterItems || []).map((it, index) => (
                          <div className="flex justify-between pb-2 text-xs text-gray-600" key={`${it.title}-${it.price}-${index.toString()}`}>
                            <div>
                              {it.title}
                            </div>
                            <div className="text-right">
                              {formatNumber(it.price, undefined, undefined, it.currencyCode)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {depositDetails}

              {/* Don't show on results page, Only display on Checkout and Confirmation page */}
              {(isCheckoutPage || isConfirmationPage) && doShowSeatSelectionWarning ? (
                <Label
                  text={(
                    <span>
                      <b>Note:</b>
                      {' '}
                      {seatSelectionWarning}
                    </span>
                  )}
                  level="warning"
                  className="text-sm mt-2"
                />
              ) : <></>}
              {doShowBookButton ? (
                <div className="w-full text-center">
                  <button
                    className={clsx('rounded-2xl w-90 my-6 px-2 py-2 text-white', { 'mb-2': tripChanges.hasChanges, 'bg-button-theme text-button-theme': !doDisableBookButton, 'bg-gray-500 cursor-not-allowed': doDisableBookButton })}
                    type="button"
                    onClick={handleBook}
                    disabled={doDisableBookButton}
                  >
                    {checkoutButtonLabel()}
                  </button>
                  {
                    doDisableBookButton && disableBookBtnTooltip
                      ? (
                        <div className="text-sm text-red-600 mb-2 -mt-2">
                          {disableBookBtnTooltip}
                        </div>
                      ) : <></>
                  }
                  {tripChanges.hasChanges && !tripChanges.doOnlyAddPaymentMethod && (
                    <Button
                      onClick={handleDiscardChanges}
                      size={Button.buttonSizes.SM}
                      className="w-90"
                      variant={Button.buttonVariants.INLINE}
                    >
                      Discard changes
                    </Button>
                  )}
                </div>
              ) : null}

              {error && (isConfirmationPage || !doShowConfirmedTrip) && !isTripSearch && <Label text={labelText} level="warning" className="text-sm" />}

              {cruiseDetails && cruiseDetails.promotion
                && (
                  <AdditionalInfoModal buttonLabel="Special promotion available">
                    <PromotionInfo info={cruiseDetails.promotion} />
                  </AdditionalInfoModal>
                )}

              {isTripSearch ? (
                <TripBookButton
                  tripId={tripId}
                  trip={trip}
                  pricing={pricing}
                  saveTripStatus={saveTripStatus}
                  saveSendTripEmailError={saveSendTripEmailError}
                  error={error}
                  buttonColorCssClass="bg-button-theme text-button-theme"
                  saveSendTripEmailStatus={saveSendTripEmailStatus}
                />
              ) : null}
              {doShowAgentCommission && totalPriceBreakdown.commission.price > 0 && (
                <div className="mt-2 p-2 rounded-lg bg-orange-200 flex justify-between">
                  <div>
                    {totalPriceBreakdown.commission.title}
                  </div>
                  <div className="text-right">
                    {cruiseTotals && cruiseTotals.commission
                      && formatAmountWithCurrency(totalPriceBreakdown.commission.commissionItems, 'amount')}
                    {(!cruiseTotals || !cruiseTotals.commission) && <CircularProgress color="secondary" className="ml-3" size={10} />}
                  </div>
                </div>
              )}
            </div>
            {hotelItinerariesArray && hotelItinerariesArray.length ? (
              <DisplayCurrencyMessage
                roomPrices={hotelItinerariesArray.map((it) => ({
                  ...it.selectedProduct.roomPrice,
                  hotelName: getHotelAndRoomNames(it),
                }))}
                className="text-gray-500 px-4 pb-4"
                isConfirmation={isConfirmation}
              />
            ) : <></>}

            {pricing && pricing.map && pricing.map((p, index) => (
              p.cruiseLineAdvisories && p.cruiseLineAdvisories.length ? (
                <Fragment key={p.roomIndex}>
                  {pricing.length > 1 && (
                    <div className="px-4 text-xs">
                      <b>
                        Stateroom
                        {' '}
                        {index + 1}
                      </b>
                    </div>
                  )}
                  <CruiseLineAdvisories
                    advisories={p.cruiseLineAdvisories}
                    className="mx-4 p-2 text-xs mb-5"
                  />
                </Fragment>
              ) : <></>
            ))}
          </>
        )}
        {hasCruisePriceBreakdownErrors && (
          <div className="p-4 text-sm">
            <div>
              <b>
                An error has ocurred while
                {' '}
                {isCheckoutPage || isConfirmationPage ? 'your latest package or service or insurance selection' : 'select cabin'}
                :
              </b>
            </div>
            <ul className="list-disc list-outside w-11/12 m-auto">
              {cruiseTotals.errors.map((item) => (
                <li key={item.code}>
                  {item.description}
                </li>
              ))}
            </ul>
            <div className="text-center">
              {!isCheckoutPage && !isConfirmationPage ? (
                <>
                  <ResetTripButton onResetClick={handleResetClick} buttonText="Reset trip" />
                  <Label icon={<Info className="p-1" />} descriptionOnHover="To continue click reset trip to clear trip" inline tooltipPlacement="top" />
                </>
              ) : (
                <div className="text-red-600 mt-3">
                  To continue please revert your latest selection
                  {' '}
                  (package/service/insurance/transportation selection).
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isTripQuote && isConfirmationPage && travelAgency && travelAgency.settings
        && travelAgency.settings.messageOnTripQuotePage ? (
          <div className={clsx('border border-orange-200 rounded-lg p-2 text-sm text-orange-600 bg-orange-100', { 'm-0 mt-4': doShowWarningOutsideTripConfig, 'my-3 m-2 mt-0': !doShowWarningOutsideTripConfig })}>
            {travelAgency.settings.messageOnTripQuotePage}
          </div>
        ) : <></>}
      {tripConfirmation && isConfirmationPage && tripConfirmation.isTripConfirmationUpdated
        && <div className="border-2 my-3 rounded-lg p-1 m-2 font-bold text-xs text-center">Trip confirmation updated</div>}
    </>
  );

  return (
    <div className={clsx('min-h-16', {
      'border-l-2 bg-body-0': isTripSearch && !doEnableTripConfigDecoupling,
      'border-2 bg-white overflow-y-auto': !isTripSearch && (!isTripQuote || (isTripQuote && !doShowWarningOutsideTripConfig)),
      'sticky top-0': !isUpdatingBookedTrip && !isTripSearch,
      'bg-body-0': isTripQuote && doShowWarningOutsideTripConfig,
      'bg-white border-l-0 mb-5 rounded-md shadow': doEnableTripConfigDecoupling,
    })}
    >
      {withSpinner(renderComponent, !trip || !cruiseTotals
        || (cruiseTotals && cruiseTotals.errors && cruiseTotals.errors.length === 0
          && trip.cruiseItinerary && (cruiseTotals.paymentSchedules || []).length === 0)
        || (trip.flightItinerary && !trip.flightItinerary.cost)
        || ((isCheckoutPage || isConfirmationPage) && pricingStatus === SearchStatus.SEARCHING))}
    </div>
  );
};

TripConfiguration.propTypes = {
  tripId: PropTypes.string.isRequired,
  isTripSearch: PropTypes.bool,
  updatedItineraries: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  doShowConfirmedTrip: PropTypes.bool,
  sectionHeader: PropTypes.string,
  onTabChange: PropTypes.func,
};

TripConfiguration.defaultProps = {
  isTripSearch: true,
  updatedItineraries: null,
  doShowConfirmedTrip: false,
  sectionHeader: null,
  onTabChange: null,
};

export default memo(TripConfiguration);
